/**
 * Шаги «Визуалы» и «Карточки» пряников — чистая логика без React: план «артикул → набор → кабинеты»,
 * смета, отбор запусков для таблицы, причины блокировок по-русски.
 */

import type { BadgeVariant } from '../../../components/ui/Badge'
import type { SellerTarget } from '../../../components/useSellerTargets'
import { isRunPending } from '../../content-funnel-series/labels'
import type { RunSummary, SeriesEstimate } from '../../content-funnel-series/types'
import { BLOCKER_HINTS } from '../visuals'
import type {
  ArticleMeta,
  CardOutcomeStatus,
  CardPlanPair,
  SkuBatchExcludedTarget,
  SkuBatchItem,
  SkuBatchTarget,
  SlideSetMatch,
  VisualsProfile,
} from './types'

/** Профиль шага «Визуалы» по умолчанию: пряники (поведение каталога и мастера пряников). */
export const DEFAULT_VISUALS_PROFILE: VisualsProfile = 'gingerbread'

/** Сколько артикулов принимает один вызов пакета запусков и плана карточек. */
export const MAX_ARTICLES_PER_CALL = 100
/** Сколько кабинетов принимает один вызов плана карточек. */
export const MAX_SELLERS_PER_CALL = 30

/** Опрос запусков, пока есть генерирующиеся. */
export const RUNS_POLL_MS = 5000

export function chunk<T>(list: T[], size: number): T[][] {
  const out: T[][] = []
  for (let index = 0; index < list.length; index += size) out.push(list.slice(index, index + size))
  return out
}

const MARKETPLACE_SHORT: Record<string, string> = { wb: 'WB', ozon: 'OZON', ym: 'ЯМ', site: 'Сайт' }

export const marketplaceShort = (marketplace: string): string => MARKETPLACE_SHORT[marketplace] ?? marketplace.toUpperCase()

/** Подпись кабинета: «WB · КСА». */
export function sellerLabel(target: Pick<SellerTarget, 'marketplace' | 'cabinet' | 'name'>): string {
  return `${marketplaceShort(target.marketplace)} · ${target.cabinet || target.name}`
}

/** Цель запуска из кабинета: без машинного кода кабинета запуск не создать (наборы и публикация адресуют кабинет кодом). */
export function skuTargetOf(seller: SellerTarget): SkuBatchTarget | null {
  return seller.cabinetCode ? { marketplace: seller.marketplace, cabinet: seller.cabinetCode } : null
}

const targetKey = (target: Pick<SkuBatchTarget, 'marketplace' | 'cabinet'>): string => `${target.marketplace}:${target.cabinet}`

// ── План «артикул → набор → кабинеты» ───────────────────────────────

export interface PlanGroup {
  setIds: number[]
  setNames: string[]
  sellers: SellerTarget[]
}

export interface ArticlePlan {
  article: string
  title: string | null
  /** Категория известна и набор подобран по ней; иначе состав определится при запуске. */
  known: boolean
  groups: PlanGroup[]
  /** Кабинеты, для которых нет набора: визуалов и карточки не будет. */
  uncovered: SellerTarget[]
}

/**
 * Ключ ответа `slide-sets/match`: категория; у макета с известным типом — «категория|тип» (наборы вроде
 * «Макеты · Круг» зависят от типа макета, и у двух макетов одной категории план может различаться).
 */
export const matchKeyOf = (categoryId: number, designType?: string | null): number | string =>
  designType ? `${categoryId}|${designType}` : categoryId

/**
 * План по ответам `slide-sets/match`: по категории — какие наборы закрывают какие кабинеты. Кабинеты с одинаковым
 * списком наборов склеиваются в одну группу (= один запуск). `matches` — ответ по id категории.
 */
export function buildVisualsPlan(
  articles: string[],
  meta: Map<string, ArticleMeta>,
  matches: Map<number | string, SlideSetMatch>,
  sellers: SellerTarget[],
): ArticlePlan[] {
  return articles.map((article) => {
    const info = meta.get(article) ?? null
    const match = info?.categoryId != null ? matches.get(matchKeyOf(info.categoryId, info.designType)) : undefined
    if (!match) return { article, title: info?.title ?? null, known: false, groups: [], uncovered: [] }
    const byKey = new Map(match.targets.map((item) => [`${item.marketplace}:${item.cabinet ?? ''}`, item.sets]))
    const groups = new Map<string, PlanGroup>()
    const uncovered: SellerTarget[] = []
    for (const seller of sellers) {
      const target = skuTargetOf(seller)
      const sets = target ? byKey.get(targetKey(target)) ?? [] : []
      if (sets.length === 0) {
        uncovered.push(seller)
        continue
      }
      const ids = sets.map((set) => set.id)
      const key = ids.join(',')
      const group = groups.get(key) ?? { setIds: ids, setNames: sets.map((set) => set.name), sellers: [] }
      group.sellers.push(seller)
      groups.set(key, group)
    }
    return { article, title: info?.title ?? null, known: true, groups: [...groups.values()], uncovered }
  })
}

/** Сколько запусков создастся: по запуску на (артикул × группа). Артикулы с неизвестной категорией — по запуску, как минимум. */
export function plannedRunCount(plan: ArticlePlan[]): number {
  return plan.reduce((sum, item) => sum + (item.known ? item.groups.length : 1), 0)
}

/** Артикулы, для которых известно, что набора нет ни на один выбранный кабинет. */
export function articlesWithoutSets(plan: ArticlePlan[]): string[] {
  return plan.filter((item) => item.known && item.groups.length === 0).map((item) => item.article)
}

// ── Смета ───────────────────────────────────────────────────────────

export interface EstimateTotal {
  /** Запусков, которые вошли в смету. */
  runs: number
  usd: number | null
  currency: string | null
  /** Цена по тарифу известна не для всех запусков. */
  partial: boolean
}

/** Группа наборов → сколько запусков на неё придётся; смета запуска умножается на это число. */
export function estimateTotal(plan: ArticlePlan[], estimates: Map<string, SeriesEstimate>): EstimateTotal {
  let runs = 0
  let usd = 0
  let priced = 0
  let currency: string | null = null
  for (const item of plan) {
    for (const group of item.groups) {
      runs += 1
      const estimate = estimates.get(group.setIds.join(','))
      if (estimate && estimate.finalUsd != null && estimate.pricingStatus === 'estimated') {
        usd += estimate.finalUsd
        priced += 1
        currency = estimate.currency ?? currency
      }
    }
  }
  return { runs, usd: priced > 0 ? usd : null, currency, partial: priced < runs }
}

/** Все группы наборов плана без повторов: по каждой нужен один запрос сметы. */
export function distinctSetGroups(plan: ArticlePlan[]): number[][] {
  const seen = new Map<string, number[]>()
  for (const item of plan) for (const group of item.groups) seen.set(group.setIds.join(','), group.setIds)
  return [...seen.values()]
}

// ── Запуски ─────────────────────────────────────────────────────────

/**
 * Запуски для таблицы: только выбранные артикулы, без отменённых; из запусков одного артикула на одни и те же
 * кабинеты остаётся самый новый (пересозданные не копятся в таблице).
 */
export function pickRuns(runs: RunSummary[], articles: string[]): RunSummary[] {
  const wanted = new Set(articles)
  const stamp = (run: RunSummary) => Date.parse(run.createdAt ?? '') || 0
  const latest = new Map<string, RunSummary>()
  for (const run of runs) {
    if (!run.article || !wanted.has(run.article) || run.state === 'cancelled') continue
    const key = `${run.article}|${[...(run.targets ?? [])].map(targetKey).sort().join(',')}`
    const current = latest.get(key)
    if (!current || stamp(run) > stamp(current)) latest.set(key, run)
  }
  const order = new Map(articles.map((article, index) => [article, index]))
  return [...latest.values()].sort(
    (a, b) => (order.get(a.article ?? '') ?? 0) - (order.get(b.article ?? '') ?? 0) || stamp(b) - stamp(a),
  )
}

export const hasPendingRuns = (runs: RunSummary[]): boolean => runs.some((run) => isRunPending(run.state))

/** Артикулы, по которым уже есть незавершённо-неудачные запуски: повторный запуск платный, его подтверждаем. */
export function articlesWithRuns(runs: RunSummary[]): string[] {
  return [...new Set(runs.filter((run) => run.state !== 'failed' && run.state !== 'cancelled').map((run) => run.article ?? '').filter(Boolean))]
}

// ── Итог запуска пакета ─────────────────────────────────────────────

export interface LaunchSummary {
  created: number
  articlesLaunched: number
  errors: Array<{ article: string; message: string; code: string }>
  excluded: Array<{ marketplace: string; cabinet: string | null; message: string; articles: number }>
}

export function summarizeLaunch(items: SkuBatchItem[]): LaunchSummary {
  const created = items.filter((item) => item.runId)
  const errors = items.filter((item) => item.error).map((item) => ({ article: item.article, message: item.error!.message, code: item.error!.code }))
  const excluded = new Map<string, { marketplace: string; cabinet: string | null; message: string; articles: Set<string> }>()
  const addExcluded = (article: string, entry: SkuBatchExcludedTarget) => {
    const key = `${entry.marketplace}:${entry.cabinet ?? ''}:${entry.message}`
    const current = excluded.get(key) ?? { marketplace: entry.marketplace, cabinet: entry.cabinet, message: entry.message, articles: new Set<string>() }
    current.articles.add(article)
    excluded.set(key, current)
  }
  for (const item of items) for (const entry of item.excludedTargets) addExcluded(item.article, entry)
  return {
    created: created.length,
    articlesLaunched: new Set(created.map((item) => item.article)).size,
    errors,
    excluded: [...excluded.values()].map((entry) => ({ marketplace: entry.marketplace, cabinet: entry.cabinet, message: entry.message, articles: entry.articles.size })),
  }
}

// ── Карточки ────────────────────────────────────────────────────────

/** Что делать с блокировками шага «Карточки» (дополняет `BLOCKER_HINTS` каталога). */
const CARD_BLOCKER_HINTS: Record<string, string> = {
  ...BLOCKER_HINTS,
  PIPELINE_VISUALS_MISSING: 'Утвердите визуалы на странице запуска: после публикации слайды появятся в кабинете товара.',
  PIPELINE_VISUALS_NOT_PUBLISHED: 'Дождитесь публикации визуалов на странице запуска и обновите проверку.',
  SEO_TEMPLATE_MISSING: 'Заведите SEO-шаблон для категории и кабинета в модуле SEO.',
  CATEGORY_NOT_FOUND: 'Назначьте товару категорию из справочника.',
}

export const cardBlockerHint = (code: string): string | null => CARD_BLOCKER_HINTS[code] ?? null

export type PairView = { label: string; variant: BadgeVariant }

/** Состояние пары в предпроверке. */
export function planPairView(pair: CardPlanPair): PairView {
  if (pair.ready) return pair.cardId ? { label: 'Обновит черновик', variant: 'blue' } : { label: 'Создаст черновик', variant: 'green' }
  if (pair.blockers.length > 0 && pair.blockers.every((blocker) => blocker.code === 'STATUS_NOT_REBUILDABLE')) {
    return { label: 'Уже не черновик', variant: 'gray' }
  }
  return { label: 'Заблокировано', variant: 'yellow' }
}

export const OUTCOME_VIEW: Record<CardOutcomeStatus, PairView> = {
  created: { label: 'Черновик создан', variant: 'green' },
  skipped: { label: 'Пропущено', variant: 'gray' },
  blocked: { label: 'Заблокировано', variant: 'yellow' },
  failed: { label: 'Ошибка', variant: 'red' },
}

/** Склеивает ответы нескольких вызовов плана (пачки артикулов × пачки кабинетов). */
export function mergePlans<T extends { pairs: unknown[]; rejectedArticles: Array<{ subject: string }>; rejectedTargets: Array<{ subject: string }> }>(parts: T[]): T {
  const unique = <R extends { subject: string }>(list: R[]): R[] => {
    const seen = new Set<string>()
    return list.filter((item) => (seen.has(item.subject) ? false : (seen.add(item.subject), true)))
  }
  return {
    pairs: parts.flatMap((part) => part.pairs),
    rejectedArticles: unique(parts.flatMap((part) => part.rejectedArticles)),
    rejectedTargets: unique(parts.flatMap((part) => part.rejectedTargets)),
  } as T
}

/** Страница «Черновики PIM» площадки; у сайта — каталог карточек сайта. */
export const DRAFTS_HREF: Record<string, string> = {
  wb: '/wildberries',
  ozon: '/ozon',
  ym: '/yandex-market',
  site: '/site-cards',
}

/** Артикулы мастера «Создать пряники»: созданные товары (пряник-штука и набор). */
export function articlesOfResult(created: Array<{ kind: string; article: string | null }>): string[] {
  return [...new Set(created.filter((entry) => (entry.kind === 'single' || entry.kind === 'kit') && entry.article).map((entry) => entry.article as string))]
}
